import {
  Badge,
  Box,
  Button,
  Flex,
  FormLabel,
  HStack,
  Switch,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { CaretDown, CaretRight, Plus } from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import AddMetricRow from "./AddMetricRow";
import EditableMetricRow from "./EditableMetricRow";
import TargetGapRow from "./TargetGapRow";
import ExpandCollapseControl from "../../../Reports/Insights/components/details/ExpandCollapseControl";
import { SCOPE_COLORS } from "../../../DataEntryPlanning/components/OrganizationPlanning/components/TaskCard";
import { buildTargetView, OFF_SCHEMA_CATEGORY } from "../../../../utils/foundationTargetRows";

const UNCATEGORISED = "Uncategorised";

/* Type scale matched to the Insights detail tables (ScopeOneDetail's ScopeOneByDimensionTable): 14px
   row text in gray.700. Set once on the table so every row shape inherits it; padding is left to each
   row so the deeper group headers keep theirs. */
const TABLE_TYPE_SCALE = {
  "& td": { fontSize: "14px", color: "gray.700" },
};

/* The shipped GHG target structure's own section names (foundation-target-structure-ghg-emissions.xlsx)
   are "SCOPE 1", "SCOPE 2" and "SCOPE 3 — BY CATEGORY" — matched by prefix, case-insensitively, since an
   organisation's own uploaded structure could spell the trailing part differently. Anything that isn't
   one of these three (ENERGY, TARGETS, ASSURANCE, off-schema, …) keeps the generic accent-square
   treatment below rather than being forced into a scope chip that doesn't apply to it. */
const scopeChipFor = (category) => {
  const normalised = String(category || "").trim().toLowerCase();
  if (normalised.startsWith("scope 1")) return SCOPE_COLORS.scope1;
  if (normalised.startsWith("scope 2")) return SCOPE_COLORS.scope2;
  if (normalised.startsWith("scope 3")) return SCOPE_COLORS.scope3;
  return null;
};

/* Whatever a category name says beyond its leading "Scope N" — e.g. "— BY CATEGORY" out of "SCOPE 3 — BY
   CATEGORY" — so the chip isn't missing information the sheet's own section name carried. Empty for a
   bare "SCOPE 1"/"SCOPE 2", where the chip's own label already says everything there is to say. */
const normaliseCategoryTail = (category) =>
  String(category || "")
    .replace(/^\s*scope\s*[123]\s*/i, "")
    .trim();

/* Same idea as the Insights detail tables (e.g. ScopeOneDetail's per-category `accent` colours): a small
   coloured square identifies the group at a glance. Those pages hard-code one accent per fixed category
   (Stationary/Mobile/Refrigerants); a target structure's sections are organisation-defined, so there is no
   fixed set to hard-code against — this derives a stable colour from the name instead, the same approach
   `CcPeoplePicker.jsx` uses for its avatar initials. */
const CATEGORY_PALETTE = [
  "#F3B85E",
  "#5EB5F3",
  "#4A8A0A",
  "#B45309",
  "#805AD5",
  "#2C7A7B",
  "#B83280",
  "#975A16",
  "#2B6CB0",
  "#DD6B20",
];
const colorForCategory = (name) => {
  const sum = Array.from(name || "").reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return CATEGORY_PALETTE[sum % CATEGORY_PALETTE.length];
};

/**
 * The metrics for one year.
 *
 * Two shapes, and which one applies is the organisation's choice rather than a display option:
 *
 * - **With a target structure** (`structure`), the table IS the structure — every datapoint asked for, with
 *   its value or a visible gap, in the sections and order the sheet was authored in, and anything the
 *   structure does not account for in a trailing group. That is what makes "what is still missing?" a
 *   question the table answers. This is also what makes the table COMPACT: with every datapoint "required",
 *   showing just Datapoint/Value (value and unit read as one field — metrics-table-2a) plus actions is
 *   enough, and provenance (Applies to/Source/Reference/Details/Confidence) plus the usage trace move
 *   into `MetricInfoOverlay`, opened from each row's (i) icon — see `EditableMetricRow`'s `compact` prop.
 *   There is no column header row for this shape: the toolbar above the table (filled-of-total count,
 *   "hide empty" switch, expand/collapse all) replaces it — a reader can already see Datapoint/Value from
 *   each row's own content, and every category group header repeats its own "x of y" already.
 * - **Without one**, it groups by the category on each row, exactly as before, and renders FULL — every
 *   column visible at once. An organisation that has not said what it wants must not be shown gaps nobody
 *   asked about, and `ExtractionReviewModal` deliberately passes no structure for the same reason it needs
 *   every column: it is reviewing one document's raw read against its source, not the required set.
 *
 * `compact` is therefore keyed on `targetView.hasStructure`, not a separate prop — a structure existing is
 * exactly what makes "required, so three columns are enough" true. See the "two column modes" note in
 * frontend/CLAUDE.md for the cell-count invariant this implies for every row shape below.
 *
 * `usageByDatapointUid` keys on the DATAPOINT uid, never on the row id: a row id names one version, and the
 * question "what depends on this figure" spans every version of it.
 *
 * A local table on purpose: this project deliberately has no shared `DataTableV2`, so a change here
 * cannot affect another page's table.
 */
export default function FoundationDataTable({
  rows,
  canEdit,
  structure,
  usageByDatapointUid,
  onShowUsage,
  onSaveRow,
  onDeleteRow,
  onAddRow,
  savingRowId,
  deletingRowId,
  isAdding,
  /* Review mode (ExtractionReviewModal): the same compact Core/Custom table, but read-only over one
     document's extraction — gaps hidden by default (a document reporting four of the datapoints is doing its
     job, not failing sixty-two), and no hide/expand toolbar. */
  reviewMode = false,
  // Review mode only: extracted row id -> compareWithCurrent's {state, current}.
  comparisons = null,
  // Called (editorId, isOpen) by every inline editor, so the page can block approval while one is open.
  onEditingChange,
  // Review mode: Set of extracted-row ids crossed out for this ingest, and the toggle.
  ignoredIds = null,
  onToggleIgnored,
}) {
  const targetView = useMemo(() => buildTargetView(structure, rows), [structure, rows]);
  const compact = targetView.hasStructure;

  const grouped = useMemo(() => {
    if (targetView.hasStructure) {
      return targetView.groups;
    }
    const byCategory = new Map();
    (rows || []).forEach((row) => {
      const key = row.category || UNCATEGORISED;
      if (!byCategory.has(key)) byCategory.set(key, []);
      byCategory.get(key).push(row);
    });
    // Rows arrive ordered by category from the server, so Map insertion order is already the display
    // order — no second sort, and so no way for the two to disagree.
    return Array.from(byCategory, ([category, categoryRows]) => ({
      category,
      rows: categoryRows,
      items: null,
    }));
  }, [rows, targetView]);

  const [collapsed, setCollapsed] = useState({});
  const [addingIn, setAddingIn] = useState(null);
  // Hides TargetGapRows and any row whose value has been cleared back to blank — "empty" means no figure
  // to look at, whether that's a datapoint never filled or one deliberately reset. Compact-mode only: the
  // full (no-structure) table has no gap concept, so there's nothing for this to hide there.
  const [hideEmpty, setHideEmpty] = useState(reviewMode);
  const effectiveHideEmpty = compact && hideEmpty;

  const toggle = (category) => setCollapsed((prev) => ({ ...prev, [category]: !prev[category] }));

  // With a structure the table splits into two tabs: Core (the structure's own sections) and Custom
  // (figures matching no datapoint in it — buildTargetView's trailing off-schema group, flattened).
  const coreGroups = useMemo(() => grouped.filter((group) => !group.offSchema), [grouped]);
  const customRows = useMemo(
    () => grouped.filter((group) => group.offSchema).flatMap((group) => group.items.flatMap((item) => item.rows)),
    [grouped]
  );
  const visibleGroups = compact ? coreGroups : grouped;

  // One flag per category covers every group — expanding/collapsing all is just resetting (or filling)
  // that same map, not a second piece of state to keep in sync with it.
  const allExpanded = visibleGroups.every((group) => !collapsed[group.category]);
  const expandAll = () => setCollapsed({});
  const collapseAll = () =>
    setCollapsed(Object.fromEntries(visibleGroups.map((group) => [group.category, true])));

  const renderMetricRow = (row) => (
    <EditableMetricRow
      key={row.id}
      row={row}
      canEdit={canEdit}
      onSave={onSaveRow}
      onDelete={onDeleteRow}
      isSaving={savingRowId === row.id}
      isDeleting={deletingRowId === row.id}
      usage={usageByDatapointUid?.[row.datapointUid]}
      onShowUsage={onShowUsage}
      compact={compact}
      comparison={comparisons?.get(row.id) ?? null}
      onEditingChange={onEditingChange}
      isIgnored={!!ignoredIds?.has(row.id)}
      onToggleIgnored={onToggleIgnored}
    />
  );

  // See the "two column modes" note in frontend/CLAUDE.md — every row variant below must total this many
  // cells (via colSpan where it isn't one real column per field).
  // Review mode drops the actions column (EditableMetricRow omits it when given a comparison).
  const columnCount = compact ? (reviewMode ? 2 : 3) : 8;

  const groupedTable = (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" overflowX="auto">
      {/* Compact (structure) mode only. Replaces the old Datapoint/Value column header, which named two
          columns a reader can already see are Datapoint/Value from their content — this instead says the
          one thing the header couldn't: how much of the WHOLE structure is filled in, not just one
          category's count (each group header already shows its own "x of y"). */}
      {/* Review mode: how much of the target structure THIS document supplies — the one number a reviewer
          needs before accepting. Same bar treatment as the Insights "Share of" columns. */}
      {compact && reviewMode ? (
        <Flex
          align="center"
          gap={4}
          px={4}
          py={3}
          bg="gray.50"
          borderBottom="1px solid"
          borderColor="gray.200"
          wrap="wrap"
        >
          <Text fontSize="14px" color="gray.700" flexShrink={0}>
            <Text as="span" fontWeight="700" color="darkTeal">
              {targetView.filled} of {targetView.total}
            </Text>{" "}
            datapoints matched from this document
          </Text>
          <Box flex="1" minW="120px" maxW="320px" h="6px" bg="gray.200" borderRadius="full" overflow="hidden">
            <Box
              h="100%"
              w={`${targetView.total > 0 ? (targetView.filled / targetView.total) * 100 : 0}%`}
              bg="lime"
              borderRadius="full"
            />
          </Box>
          <FormLabel
            htmlFor="show-unmatched-datapoints"
            mb="0"
            ml="auto"
            fontSize="13px"
            color="gray.600"
            display="flex"
            alignItems="center"
            gap={2}
          >
            Show unfound
            <Switch
              id="show-unmatched-datapoints"
              size="sm"
              colorScheme="limeShade"
              isChecked={!hideEmpty}
              onChange={(e) => setHideEmpty(!e.target.checked)}
            />
          </FormLabel>
        </Flex>
      ) : null}
      {compact && !reviewMode ? (
        <Flex
          align="center"
          justify="flex-end"
          gap={3}
          px={4}
          py={3}
          bg="gray.50"
          borderBottom="1px solid"
          borderColor="gray.200"
          wrap="wrap"
        >
          <HStack spacing={4}>
            <FormLabel htmlFor="hide-empty-datapoints" mb="0" fontSize="13px" color="gray.600" display="flex" alignItems="center" gap={2}>
              Hide empty
              <Switch
                id="hide-empty-datapoints"
                size="sm"
                colorScheme="limeShade"
                isChecked={hideEmpty}
                onChange={(e) => setHideEmpty(e.target.checked)}
              />
            </FormLabel>
            <ExpandCollapseControl
              allExpanded={allExpanded}
              onExpandAll={expandAll}
              onCollapseAll={collapseAll}
            />
          </HStack>
        </Flex>
      ) : null}
      <Table size="sm" variant="simple" sx={TABLE_TYPE_SCALE}>
        {compact ? null : (
          <Thead bg="gray.50">
            {/* Caption-style header cells — uppercase, 11px, gray.500 — matching the Insights "By
                dimension" tables (e.g. ScopeOneDetail's ScopeOneByDimensionTable) rather than Chakra's
                default Th styling, so this table reads as the same family as the rest of the app. */}
            <Tr>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600">
                Metric
              </Th>
              <Th py={3} px={4} isNumeric fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600">
                Value
              </Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600">
                Unit
              </Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600">
                Applies to
              </Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600">
                Source
              </Th>
              {/* The quote the figure was read from. Its own column rather than a tooltip: it is what lets a
                  reviewer check a number without opening the document, which is the most common thing anyone
                  will want to do with this table. */}
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600">
                Reference
              </Th>
              {/* Whatever the agent recorded that no other field covers — qualifiers like location- vs
                  market-based. Invisible until now, despite the prompt asking for it. */}
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600">
                Details
              </Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600">
                Confidence
              </Th>
            </Tr>
          </Thead>
        )}
        <Tbody>
          {visibleGroups.map((group) => {
            const { category, items, offSchema } = group;
            const isCollapsed = !!collapsed[category];
            // In structure mode a group counts DATAPOINTS and says how many are filled; without one it
            // counts rows, which is all there is to count.
            const filledInGroup = items
              ? items.filter((item) => item.rows.length > 0).length
              : null;
            const count = items ? items.length : group.rows.length;

            const accent = colorForCategory(category);
            const scopeChip = !offSchema ? scopeChipFor(category) : null;

            return [
              /* Same look as the Insights "By dimension" tables' group/expand rows (e.g. ScopeOneDetail's
                 ScopeOneByDimensionTable): light gray.50 row, a small coloured square identifying the
                 group, dark uppercase caption text. Off-schema keeps its own orange treatment — that is a
                 warning state, not a category colour.

                 The chevron and the label live in ONE cell (not a separate `<Td>` each) so the label's
                 left edge is governed by flex layout, not by the table's own column-width algorithm. Two
                 real columns drift apart the moment a group opens: an HTML table sizes a column to its
                 widest cell, and the rows underneath (EditableMetricRow, TargetGapRow) have no matching
                 leading icon column of their own — their first cell IS the datapoint name, often much
                 wider than a bare chevron. Opening a group widens that first column to fit those names
                 and pushes a chevron sitting in its own `<Td>` wider than the label beside it, which then
                 no longer starts where the chevron does. A single cell has no such second column to drift.

                 Scope 1/2/3 sections get the SAME chip used everywhere else a task is tagged by scope
                 (TaskCard's SCOPE_COLORS) instead of the generic accent square — "Scope 1" means the same
                 thing on this page as it does on the data-entry task cards, so it should look the same. */
              <Tr key={`group-${category}`} bg={offSchema ? "orange.50" : "gray.50"}>
                <Td colSpan={columnCount} py={4} px={4}>
                  <HStack spacing={2} justify="space-between">
                    <HStack
                      spacing={2}
                      cursor="pointer"
                      onClick={() => toggle(category)}
                      role="button"
                      tabIndex={0}
                      aria-label={isCollapsed ? `Expand ${category}` : `Collapse ${category}`}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggle(category);
                        }
                      }}
                      flex="1"
                      minW={0}
                    >
                      {isCollapsed ? (
                        <CaretRight size={12} weight="bold" style={{ flexShrink: 0 }} />
                      ) : (
                        <CaretDown size={12} weight="bold" style={{ flexShrink: 0 }} />
                      )}
                      {scopeChip ? (
                        <Box
                          display="inline-flex"
                          alignItems="center"
                          gap="4px"
                          bg={scopeChip.light}
                          color={scopeChip.text}
                          fontSize="13px"
                          fontWeight="700"
                          px="10px"
                          h="26px"
                          borderRadius="full"
                          flexShrink={0}
                          whiteSpace="nowrap"
                        >
                          <scopeChip.icon size={14} weight="fill" style={{ flexShrink: 0 }} />
                          {scopeChip.label}
                        </Box>
                      ) : (
                        <>
                          {!offSchema ? <Box w="8px" h="8px" bg={accent} borderRadius="sm" flexShrink={0} /> : null}
                          <Text
                            fontSize="13px"
                            fontWeight="700"
                            color={offSchema ? "orange.700" : "darkTeal"}
                            textTransform="uppercase"
                            letterSpacing="0.04em"
                          >
                            {category}
                          </Text>
                        </>
                      )}
                      {/* The chip carries its own scope name ("Scope 1"), so the raw category text sits
                          alongside it only when the sheet's own wording says more than that (e.g. "SCOPE 3
                          — BY CATEGORY") — otherwise it would just repeat the chip's label. */}
                      {scopeChip && normaliseCategoryTail(category) ? (
                        <Text fontSize="13px" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.04em">
                          {normaliseCategoryTail(category)}
                        </Text>
                      ) : null}
                      <Badge variant="subtle" colorScheme={offSchema ? "orange" : "gray"} fontSize="11px">
                        {filledInGroup == null ? count : `${filledInGroup} of ${count}`}
                      </Badge>
                    </HStack>
                  </HStack>
                </Td>
              </Tr>,
              ...(isCollapsed
                ? []
                : (items ?? group.rows.map((row) => ({ key: `row-${row.id}`, target: null, rows: [row] })))
                    .flatMap((item) =>
                      item.rows.length === 0
                        ? effectiveHideEmpty
                          ? []
                          : [
                              /* Fills the gap in place — clicking the "Not reported" field turns THIS row
                                 into an inline editor (see TargetGapRow's own javadoc) rather than opening a
                                 separate AddMetricRow underneath it. `category` is passed through because
                                 the add payload needs it and a gap, unlike a filled row, carries no row of
                                 its own to read it off. */
                              <TargetGapRow
                                key={item.key}
                                target={item.target}
                                category={category}
                                canEdit={canEdit}
                                onAdd={onAddRow}
                                onEditingChange={onEditingChange}
                                isAdding={isAdding}
                                reviewMode={reviewMode}
                              />,
                            ]
                        : item.rows
                            // A row can be matched to a datapoint and still carry no value — cleared back
                            // to blank (EditableMetricRow's compact "clear" action), rather than deleted. To
                            // "hide empty" that reads the same as a gap: nothing to look at either way.
                            .filter((row) => !effectiveHideEmpty || (row.dataValue != null && row.dataValue !== ""))
                            .map(renderMetricRow)
                    )),
              ...(addingIn === category
                ? [
                    <AddMetricRow
                      key={`add-${category}`}
                      defaultCategory={category === UNCATEGORISED ? "" : category}
                      onAdd={onAddRow}
                onEditingChange={onEditingChange}
                      onCancel={() => setAddingIn(null)}
                      isAdding={isAdding}
                      compact={compact}
                    />,
                  ]
                : []),
            ];
          })}

          {/* Adding into a year that has no categories yet, where there is no group header to hang an
              Add button off. */}
          {canEdit && visibleGroups.length === 0 ? (
            addingIn === UNCATEGORISED ? (
              <AddMetricRow
                defaultCategory=""
                onAdd={onAddRow}
                onEditingChange={onEditingChange}
                onCancel={() => setAddingIn(null)}
                isAdding={isAdding}
                compact={compact}
              />
            ) : (
              <Tr>
                <Td colSpan={columnCount}>
                  <Button
                    size="xs"
                    variant="ghost"
                    leftIcon={<Plus size={12} />}
                    onClick={() => setAddingIn(UNCATEGORISED)}
                  >
                    Add a metric
                  </Button>
                </Td>
              </Tr>
            )
          ) : null}
        </Tbody>
      </Table>
    </Box>
  );

  if (!compact) return groupedTable;

  // Custom: figures kept at promotion that match no datapoint in the structure. A flat list — they have
  // no section of their own — in the same compact row shape as Core.
  const customTable = (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" overflowX="auto">
      <Table size="sm" variant="simple" sx={TABLE_TYPE_SCALE}>
        <Tbody>
          {customRows.map(renderMetricRow)}
          {addingIn === OFF_SCHEMA_CATEGORY ? (
            <AddMetricRow
              defaultCategory=""
              onAdd={onAddRow}
                onEditingChange={onEditingChange}
              onCancel={() => setAddingIn(null)}
              isAdding={isAdding}
              compact
            />
          ) : canEdit ? (
            <Tr>
              <Td colSpan={columnCount}>
                <Button
                  size="xs"
                  variant="ghost"
                  leftIcon={<Plus size={12} />}
                  onClick={() => setAddingIn(OFF_SCHEMA_CATEGORY)}
                >
                  Add a custom metric
                </Button>
              </Td>
            </Tr>
          ) : customRows.length === 0 ? (
            <Tr>
              <Td colSpan={columnCount}>
                <Text fontSize="sm" color="gray.500">
                  No custom metrics.
                </Text>
              </Td>
            </Tr>
          ) : null}
        </Tbody>
      </Table>
    </Box>
  );

  return (
    <Tabs variant="primary" isLazy>
      <TabList>
        <Tab>
          Core
          <Badge ml={2} variant="subtle" colorScheme="gray">
            {targetView.filled} of {targetView.total}
          </Badge>
        </Tab>
        <Tab>
          Custom
          <Badge ml={2} variant="subtle" colorScheme="gray">
            {customRows.length}
          </Badge>
        </Tab>
      </TabList>
      <TabPanels>
        <TabPanel px={0}>
          <TabExplainer>
            Core metrics are the common set needed to report across the main reporting frameworks. Each one is
            mapped closely, so you can see at a glance which are available and where each stands.
          </TabExplainer>
          {groupedTable}
        </TabPanel>
        <TabPanel px={0}>
          <TabExplainer>
            Custom metrics go beyond the core set — further figures identified in your sources. Keep the ones
            that could be useful in reporting: once saved, our AI can draw on them when drafting disclosures.
          </TabExplainer>
          {customTable}
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
}

/** One-line description of what a tab holds, shown above its table (the visualisation-wrapper convention). */
function TabExplainer({ children }) {
  return (
    <Text fontSize="13px" color="gray.500" mb={3} maxW="760px">
      {children}
    </Text>
  );
}
